import { get, set, del, keys } from "idb-keyval";
import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";
import { getCachedRequirementList, getClientList } from "./requirementCached";
import Papa from "papaparse";

/**
 * Calculates the timestamp for the next Sunday at 00:00
 */
function getNextSundayTimestamp() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  // Si hoy es domingo, sumar 7 días para el próximo domingo
  const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
  const nextSunday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + daysUntilSunday,
    0, 0, 0, 0
  );
  return nextSunday.getTime();
}

export async function getRequirementsByClient(clientId) {

  // console.log("getRequirementsByClient called with clientId:", clientId);

  // Key for the cache
  const cacheKey = `requirementsByClient_${clientId}`;
  const cacheMetaKey = `requirementsByClient_${clientId}_meta`;
  const cached = await get(cacheKey);
  const meta = await get(cacheMetaKey);
  const now = Date.now();

  let mapped = {};
  if (cached && meta && meta.expiry > now) {
    mapped = cached;
  } else {

    // console.log("Downloading requirements for client:", clientId);

    // Fetch the requirements from the server
    const response = await fetch(
      `${BASE_URL}insurance/export-requirements-by-client-csv/?client_id=${clientId}`,
      { headers: { ...getAuthHeader() } }
    );

    if (!response.ok) {
      console.error("Failed to download requirements CSV for client:", clientId);
      return {};
    }

    const csvText = await response.text();
    const { data } = Papa.parse(csvText, { header: true, skipEmptyLines: true });

    // Group requirements by medical center
    data.forEach(item => {
      const medicalCenter = Number(item.medical_center);
      if (!mapped[medicalCenter]) {
        mapped[medicalCenter] = [];
      }
      mapped[medicalCenter].push(Number(item.requirement));
    });

    // Save the requirements in a mapped format
    await set(cacheKey, mapped);
    await set(cacheMetaKey, { expiry: getNextSundayTimestamp() });
  }

  return mapped;
}

/**
 * Builds the final structure of requirements by client and center.
 */
function buildRequirementStructure(mapped, requirementList, centerName) {

  // console.log("Building requirement structure with mapped data: ", centerName);
  // console.log("Building requirement structure with mapped data: ", mapped);

  if (!Array.isArray(mapped) || mapped.length === 0) return [];

  const result = [];

  for (const client of mapped) {
    const { clientId, clientName, medicalCenter, requirements } = client;

    // requirements is already filtered by centerId
    for (const reqId of requirements) {
      const reqData = requirementList.find(r => Number(r.id) === Number(reqId));
      if (!reqData) continue;
      result.push({
        company_client: Number(clientId),
        company_client_name: clientName,
        id: Number(reqData.id),
        medical_center: Number(medicalCenter),
        medical_center_name: centerName,
        name: String(reqData.requirement_name),
        requirement: Number(reqData.requirement),
        requirement_day: [],
        requirement_name: reqData.requirement_name,
        sub_administrator: null,
        type_requirement: Number(reqData.type_requirement),
        type_requirement_name: reqData.type_requirement_name,
      });
    }
  }

  // console.log("Final requirement structure: ", result);

  return result;
}

/**
 * Retrieve the requirements for a given client and center.
 */
export async function getCachedRequirementsByClientAndCenter({ clientId, centerId, centerName } = {}) {

  // console.log ("getCachedRequirementsByClientAndCenter called with: ", { clientId, centerId, centerName });

  // Map requirements by client and center
  let mapped = [];

  // Retrieve the list of clients
  const clientListResponse = await getClientList();
  const clientList = clientListResponse?.data || [];
  if (!clientList || clientList.length === 0) {
    console.error("No clients found to map requirements against.");
    return [];
  }

  // Retrieve the list of cached requirements
  const requirementList = await getCachedRequirementList();
  if (!requirementList || requirementList.length === 0) {
    console.error("No requirements found to map against.");
    return [];
  }

  try {

    // For each client, fetch their requirements
    for (const client of clientList) {

      const clientId = client.id;
      const clientName = client.name;

      // console.log('Fetching requirements for client: ', clientName);

      // Retrieve the requirements for the client
      const requirements = await getRequirementsByClient(clientId);

      // console.log('Requirements fetched for client: ', clientName, requirements);

      // Output of requirements example:
      // {
      //   1: [1, 2, 3],
      //   2: [4, 5],
      //   3: [6, 7, 8]
      // }
      // This means that for each medical center (1, 2, 3), 
      // we have an array of requirement IDs.

      mapped.push({
        clientId: clientId,
        clientName: clientName,
        requirements: requirements
      });

      // Map it with the clientId, example:
      // {
      //   clientId: 1,
      //   clientName: "Client A",
      //   requirements: {
      //     1: [1, 2, 3],
      //     2: [4, 5],
      //     3: [6, 7, 8]
      //   }
      // }
      // This means that for Client A, we have requirements 
      // grouped by medical center.
    }

    // If clientId doesn't exist, return empty array
    if (!clientId) {
      return [];
    }

    // Filter by clientId and centerId
    if (clientId) {
      mapped = mapped.filter(item => item.clientId === clientId);
    }

    if (centerId) {
      mapped = mapped.map(item => {
        return {
          ...item,
          requirements: item.requirements[centerId] || [],
          medicalCenter: centerId,
        };
      });
    }

  } catch (error) {
    console.error("Error clearing cached requirements: ", error);
    return [];
  }

  // console.log("Mapped requirements: ", mapped);

  const finalStructure = buildRequirementStructure(mapped, requirementList, centerName);

  // Build the final structure
  return finalStructure;
}

/**
 * Elimina todas las claves de requerimientos de idb-keyval
 */
export async function clearRequirementsCache() {
  const allKeys = await keys();
  const requirementKeys = allKeys.filter(
    (key) =>
      typeof key === "string" &&
      (key.startsWith("requirementsByClient_") || key.startsWith("requirementsByClient_") || key.startsWith("requirementsByClient_"))
  );
  await Promise.all(requirementKeys.map((key) => del(key)));
  // console.log("Requerimientos en caché eliminados:", requirementKeys);
}

/**
 * Elimina todas las claves de listado de requerimientos de idb-keyval
 */
export async function clearListRequirementsCache() {
  const allKeys = await keys();
  const requirementKeys = allKeys.filter(
    (key) =>
      typeof key === "string" &&
      (key === "requirementList" || key === "requirementListMeta")
  );
  await Promise.all(requirementKeys.map((key) => del(key)));

}

// /**
//  * Compare two requirement arrays and log differences.
//  * @param {Array} arr1 - First array (API).
//  * @param {Array} arr2 - Second array (IDB).
//  */
// function debugCompareRequirements(arr1, arr2) {
//   if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
//     console.log("One or both arrays are not valid:", { arr1, arr2 });
//     return;
//   }

//   console.log(`Comparing requirements: arr1.length=${arr1.length}, arr2.length=${arr2.length}`);
//   if (arr1.length !== arr2.length) {
//     console.warn("Arrays have different lengths!");
//   }

//   arr1.forEach((req1, idx) => {
//     const match = arr2.find(
//       (req2) =>
//         req2.requirement === req1.requirement &&
//         req2.company_client === req1.company_client &&
//         req2.medical_center === req1.medical_center &&
//         req2.type_requirement === req1.type_requirement
//     );
//     if (!match) {
//       console.warn(`No match in arr2 for arr1[${idx}]`, req1);
//     }
//   });

//   arr2.forEach((req2, idx) => {
//     const match = arr1.find(
//       (req1) =>
//         req1.requirement === req2.requirement &&
//         req1.company_client === req2.company_client &&
//         req1.medical_center === req2.medical_center &&
//         req1.type_requirement === req2.type_requirement
//     );
//     if (!match) {
//       console.warn(`No match in arr1 for arr2[${idx}]`, req2);
//     }
//   });

//   console.log("Comparison complete.");
// }

// Use this effect inside a React component to debug the requirements
/*
  useEffect(() => {
    if (
      !requirementMedicalCenters.isLoading &&
      !requirementMedicalCentersIdb.isLoading &&
      requirementMedicalCenters.data &&
      requirementMedicalCentersIdb.data
    ) {
      debugCompareRequirements(
        requirementMedicalCenters.data,
        requirementMedicalCentersIdb.data
      );
    }
  }, [
    requirementMedicalCenters.isLoading,
    requirementMedicalCentersIdb.isLoading,
    requirementMedicalCenters.data,
    requirementMedicalCentersIdb.data,
  ]);
*/